import React from "react";

import { X } from "../../icons/svg/outline";

const CHIP_BASE_CLASS =
  "inline-flex max-w-full items-center gap-1 rounded bg-teal-600 px-2 py-0.5 text-xs font-medium font-inter text-white";

/**
 * Teal selection / suggestion chip used across forms and filters.
 *
 * Renders as a ``<button>`` when ``onClick`` is set, otherwise a ``<span>``.
 * Optional ``count`` appends `` (n)``. Optional ``onRemove`` shows an X control.
 *
 * @param {Object} props
 * @param {string} props.label - Primary chip text.
 * @param {number|string|null} [props.count] - Optional count shown as `` (n)``.
 * @param {() => void} [props.onClick] - Makes the chip clickable (suggestion).
 * @param {(event: React.MouseEvent) => void} [props.onRemove] - Shows remove (X) control.
 * @param {boolean} [props.disabled=false]
 * @param {string} [props.className=""] - Extra classes on the root.
 * @param {string} [props.ariaLabel] - Accessible name; defaults to label (+ count).
 * @returns {JSX.Element}
 */
const Chip = ({
  label,
  count,
  onClick,
  onRemove,
  disabled = false,
  className = "",
  ariaLabel,
}) => {
  const hasCount = count != null && count !== "";
  const displayLabel = hasCount ? `${label} (${count})` : label;
  const accessibleName = ariaLabel || displayLabel;

  const rootClassName = [
    CHIP_BASE_CLASS,
    onClick && !disabled ? "transition hover:bg-teal-700" : "",
    disabled ? "opacity-60" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const body = (
    <>
      <span className="truncate">{displayLabel}</span>
      {onRemove && !disabled ? (
        <button
          type="button"
          className="shrink-0 rounded p-[1px] hover:bg-teal-900"
          aria-label={`Remove ${displayLabel}`}
          onClick={(event) => {
            event.stopPropagation();
            onRemove(event);
          }}
        >
          <X className="h-3 w-3" aria-hidden />
        </button>
      ) : null}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        disabled={disabled}
        aria-label={accessibleName}
        onClick={onClick}
        className={rootClassName}
      >
        {body}
      </button>
    );
  }

  return (
    <span className={rootClassName} aria-label={accessibleName}>
      {body}
    </span>
  );
};

export default Chip;
